import { z } from "zod";
import { DocumentDiagnosticServiceBindingSchema } from "./diagnostic-services.js";
import { FormOrderSetIdSchema } from "./form-order-sets.js";
import { ManualOrderFormRefSchema, ManualOrderIdSchema } from "./manual-order-marker.js";
import { ImagingContrastSchema, ImagingFacilityFormReferenceSchema, ImagingLateralitySchema, ImagingModalitySchema, } from "./orders.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
const RuleIdentifierSchema = z.string().regex(/^[a-z][a-z0-9_.-]{2,79}$/);
const NonEmptyLimitedStringSchema = z.string().trim().min(1).max(2_000);
const VersionSchema = z.number().int().positive();
export const DocumentCategorySchema = z.enum([
    "lab",
    "public_health_lab",
    "imaging",
    "public_health_follow_up",
    "work_school",
    "other",
]);
export const DocumentStatusSchema = z.enum(["draft", "active", "inactive", "archived"]);
export const DocumentVersionStatusSchema = z.enum([
    "draft",
    "scan_pending",
    "analysis_pending",
    "review_required",
    "published",
    "superseded",
    "rejected",
]);
export const DocumentPagePolicySchema = z.enum([
    "required",
    "optional_default_on",
    "optional_default_off",
    "conditional",
    "reference_only",
]);
export const DocumentPageRoleSchema = z.enum([
    "operational",
    "reference_guide",
    "patient_instruction",
    "continuation",
    "other",
]);
export const DocumentFieldTypeSchema = z.enum([
    "text",
    "multiline_text",
    "checkbox",
    "radio",
    "dropdown",
    "date",
    "provider_signature",
    "patient_attestation",
    "clinic_stamp",
    "manual_only",
    "laboratory_use_only",
]);
export const DocumentFieldSourceKindSchema = z.enum([
    "patient",
    "clinic",
    "provider",
    "encounter",
    "structured_order",
    "manual_document_input",
    "deterministic_transform",
]);
export const DocumentStorageObjectRefSchema = z
    .object({
    bucket: z.string().min(1).max(80),
    objectName: z.string().min(1).max(1_000),
    objectVersion: z.string().min(1).max(240).optional(),
    contentHash: Sha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(100 * 1024 * 1024),
    contentType: z.string().min(1).max(160),
})
    .strict();
export const DocumentRectangleSchema = z
    .object({
    x: z.number().finite().nonnegative(),
    y: z.number().finite().nonnegative(),
    width: z.number().finite().positive(),
    height: z.number().finite().positive(),
    coordinateSpace: z.enum(["pdf_points", "normalized"]),
})
    .strict()
    .superRefine((rectangle, context) => {
    if (rectangle.coordinateSpace === "normalized" &&
        (rectangle.x > 1 ||
            rectangle.y > 1 ||
            rectangle.width > 1 ||
            rectangle.height > 1 ||
            rectangle.x + rectangle.width > 1 ||
            rectangle.y + rectangle.height > 1)) {
        context.addIssue({
            code: "custom",
            message: "Normalized rectangles must remain within the unit page",
        });
    }
});
export const DocumentFieldSourceSchema = z
    .object({
    kind: DocumentFieldSourceKindSchema,
    path: z.string().min(1).max(240).optional(),
    transform: RuleIdentifierSchema.optional(),
})
    .strict()
    .superRefine((source, context) => {
    if (source.kind === "manual_document_input" && source.path !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["path"],
            message: "Manual document input cannot claim an authoritative source path",
        });
    }
    if (source.kind !== "manual_document_input" && source.path === undefined) {
        context.addIssue({
            code: "custom",
            path: ["path"],
            message: "Mapped document fields require a controlled source path",
        });
    }
    if (source.kind === "deterministic_transform" && source.transform === undefined) {
        context.addIssue({
            code: "custom",
            path: ["transform"],
            message: "Deterministic transform fields require a named transform",
        });
    }
});
export const DocumentFieldOrderCodeSchema = z
    .object({
    code: z.string().trim().min(1).max(60),
    codeSystem: z.enum(["LOINC", "local", "RadLex"]),
    display: z.string().trim().min(1).max(240),
    /**
     * "mapper_proposed" means a machine picked this code and NO human has
     * confirmed it. Drafts stay saveable while codes are unconfirmed; the
     * confirmation gate is at publish, not here, so an administrator can work
     * through a 112-field form incrementally.
     */
    origin: z.enum(["mapper_proposed", "human_confirmed"]),
})
    .strict();
/** A form checkbox's proposed meaning, reviewed independently of the PDF widget itself. */
export const DocumentImagingStudyBindingSchema = z
    .object({
    studyName: z.string().trim().min(1).max(240),
    modality: ImagingModalitySchema,
    bodySite: z.string().trim().min(1).max(160),
    /** "any" is for a visible parent study box accompanied by separate L/R/B boxes. */
    laterality: z.union([ImagingLateralitySchema, z.literal("any")]),
    contrast: ImagingContrastSchema.optional(),
    code: z.string().trim().min(1).max(80).optional(),
    codeSystem: z.enum(["RadLex", "LOINC", "local"]).optional(),
    origin: z.enum(["mapper_proposed", "human_confirmed"]),
    confidence: z.number().min(0).max(1).optional(),
    evidence: z
        .array(z
        .object({
        pageNumber: z.number().int().positive(),
        description: z.string().trim().min(1).max(1_000),
        source: z.enum(["native_widget", "page_text", "page_vision"]),
    })
        .strict())
        .max(20)
        .optional(),
})
    .strict()
    .superRefine((binding, context) => {
    if ((binding.code === undefined) !== (binding.codeSystem === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["code"],
            message: "An imaging code and code system must be supplied together",
        });
    }
    if (binding.origin === "human_confirmed" && binding.laterality === "unknown") {
        context.addIssue({
            code: "custom",
            path: ["laterality"],
            message: "A confirmed imaging checkbox needs an explicit laterality",
        });
    }
});
/** Exact, server-projected identity of one study in the reviewed imaging order. */
export const DocumentImagingStudyIdentitySchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    codeSystem: z.enum(["RadLex", "LOINC", "local", "free_text"]),
    display: z.string().trim().min(1).max(240),
    modality: ImagingModalitySchema,
    bodySite: z.string().trim().min(1).max(160),
    laterality: ImagingLateralitySchema,
    contrast: ImagingContrastSchema,
})
    .strict();
export const ClinicDocumentFieldBindingSchema = z
    .object({
    fieldId: RuleIdentifierSchema,
    fieldPath: z.string().min(1).max(500),
    pageNumber: z.number().int().positive(),
    widgetIndex: z.number().int().nonnegative(),
    rectangle: DocumentRectangleSchema,
    /** Reviewed output placement; rectangle continues to identify the original PDF widget. */
    placementRectangle: DocumentRectangleSchema.optional(),
    fieldType: DocumentFieldTypeSchema,
    label: z.string().min(1).max(240),
    groupId: RuleIdentifierSchema.optional(),
    exportOnState: z.string().min(1).max(240).optional(),
    exportOffState: z.string().min(1).max(240).optional(),
    choiceOptions: z.array(z.string().min(1).max(240)).max(100).default([]),
    source: DocumentFieldSourceSchema,
    /**
     * The laboratory test this checkbox IS, as a code. Present only on
     * structured-order test checkboxes.
     *
     * ABSENT IS A SUPPORTED, PERMANENT STATE — not a migration gap. Every
     * already-published binding has no code and must keep resolving through the
     * legacy fuzzy display-term match exactly as it does today. Deliberately
     * `.optional()` with NO default: a default would materialize on
     * canonicalization and rewrite the contentHash of every stored version,
     * which would 409 every subsequent draft save.
     */
    orderCode: DocumentFieldOrderCodeSchema.optional(),
    imagingStudyBinding: DocumentImagingStudyBindingSchema.optional(),
    diagnosticServiceBinding: DocumentDiagnosticServiceBindingSchema.optional(),
    required: z.boolean(),
    authoritative: z.boolean(),
    readOnlyAtRuntime: z.boolean(),
    reviewStatus: z.enum(["proposed", "reviewed", "rejected"]),
    /**
     * The form editor's descriptive disposition, persisted alongside the source/authority flags
     * without an injected default, which would change hashes of already-published versions. An
     * "unused" field is never filled: the renderer omits it at runtime.
     */
    disposition: z.enum(["automatic", "manual", "unused"]).optional(),
})
    .strict()
    .superRefine((field, context) => {
    const isCheckable = field.fieldType === "checkbox" || field.fieldType === "radio";
    if (isCheckable && field.exportOnState === undefined) {
        context.addIssue({
            code: "custom",
            path: ["exportOnState"],
            message: "Checkable fields require their exact PDF export/on-state",
        });
    }
    if (!isCheckable && (field.exportOnState !== undefined || field.exportOffState !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["exportOnState"],
            message: "Only checkbox and radio fields may define export states",
        });
    }
    if (field.authoritative && !field.readOnlyAtRuntime) {
        context.addIssue({
            code: "custom",
            path: ["readOnlyAtRuntime"],
            message: "Authoritative mapped fields must be read-only at runtime",
        });
    }
    const isStructuredTestBinding = field.source.kind === "structured_order" &&
        (field.source.path ?? "").startsWith("structured_order.tests.") &&
        field.source.path !== "structured_order.tests.glucose.collection_state";
    if (field.orderCode !== undefined && !(isStructuredTestBinding && isCheckable)) {
        context.addIssue({
            code: "custom",
            path: ["orderCode"],
            message: "Only structured-order laboratory test checkboxes may carry a test code",
        });
    }
    if (field.imagingStudyBinding !== undefined &&
        !(isCheckable &&
            field.source.kind === "structured_order" &&
            field.source.path === "structured_order.imaging_study")) {
        context.addIssue({
            code: "custom",
            path: ["imagingStudyBinding"],
            message: "Only structured imaging-study checkboxes may carry an imaging binding",
        });
    }
    if (field.source.kind === "structured_order" &&
        field.source.path === "structured_order.imaging_study" &&
        field.imagingStudyBinding === undefined) {
        context.addIssue({
            code: "custom",
            path: ["imagingStudyBinding"],
            message: "An imaging-study checkbox requires a study binding",
        });
    }
    const isDiagnosticSource = field.source.kind === "structured_order" &&
        field.source.path === "structured_order.diagnostic_service";
    if (field.diagnosticServiceBinding && !(isCheckable && isDiagnosticSource))
        context.addIssue({
            code: "custom",
            path: ["diagnosticServiceBinding"],
            message: "Only structured diagnostic-service checkboxes may carry a service binding",
        });
    if (isDiagnosticSource && !field.diagnosticServiceBinding)
        context.addIssue({
            code: "custom",
            path: ["diagnosticServiceBinding"],
            message: "A diagnostic-service checkbox requires its exact service binding",
        });
    if (field.diagnosticServiceBinding &&
        ((field.disposition !== "manual" && field.disposition !== "unused") ||
            field.authoritative ||
            field.readOnlyAtRuntime))
        context.addIssue({
            code: "custom",
            path: ["disposition"],
            message: "Registered diagnostic services must remain manually completed until diagnostic ordering is enabled",
        });
    if (["patient_attestation", "manual_only", "laboratory_use_only"].includes(field.fieldType) &&
        field.source.kind !== "manual_document_input") {
        context.addIssue({
            code: "custom",
            path: ["source", "kind"],
            message: "Patient-attestation, manual-only, and laboratory-use fields cannot be auto-filled",
        });
    }
});
export const ClinicDocumentPageManifestEntrySchema = z
    .object({
    pageNumber: z.number().int().positive(),
    outputOrder: z.number().int().positive(),
    label: z.string().min(1).max(160),
    role: DocumentPageRoleSchema,
    policy: DocumentPagePolicySchema,
    conditionExpression: z.string().min(1).max(1_000).optional(),
    rotationDegrees: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
    includeInMapperContext: z.boolean(),
    includeInAdminPreview: z.boolean(),
    includeInPatientOutput: z.boolean(),
    includeInCombinedBundle: z.boolean(),
})
    .strict()
    .superRefine((page, context) => {
    if (page.policy === "conditional" && page.conditionExpression === undefined) {
        context.addIssue({
            code: "custom",
            path: ["conditionExpression"],
            message: "Conditional pages require a deterministic condition expression",
        });
    }
    if (page.policy !== "conditional" && page.conditionExpression !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["conditionExpression"],
            message: "Only conditional pages may define a condition expression",
        });
    }
    if (page.policy === "required" &&
        (!page.includeInPatientOutput || !page.includeInCombinedBundle)) {
        context.addIssue({
            code: "custom",
            message: "Required pages cannot be silenced from patient or combined output",
        });
    }
    if (page.policy === "reference_only" &&
        (page.includeInPatientOutput || page.includeInCombinedBundle)) {
        context.addIssue({
            code: "custom",
            message: "Reference-only pages must be excluded from patient and combined output",
        });
    }
});
/** Only a fully disabled reference page can exempt its retained inventory from field review. */
export function isExplicitlyExcludedClinicDocumentPage(page) {
    return (page !== undefined &&
        (page.policy === "reference_only" ||
            page.role === "reference_guide" ||
            page.role === "patient_instruction") &&
        !page.includeInMapperContext &&
        !page.includeInPatientOutput &&
        !page.includeInCombinedBundle);
}
export const ClinicDocumentPageManifestSchema = z
    .object({
    sourcePageCount: z.number().int().positive().max(500),
    pages: z.array(ClinicDocumentPageManifestEntrySchema).min(1).max(500),
})
    .strict()
    .superRefine((manifest, context) => {
    if (manifest.pages.length !== manifest.sourcePageCount) {
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Every immutable source page must remain represented in the manifest",
        });
    }
    const pageNumbers = new Set();
    const outputOrders = new Set();
    for (const [index, page] of manifest.pages.entries()) {
        if (page.pageNumber > manifest.sourcePageCount) {
            context.addIssue({
                code: "custom",
                path: ["pages", index, "pageNumber"],
                message: "Page number exceeds the immutable source page count",
            });
        }
        if (pageNumbers.has(page.pageNumber)) {
            context.addIssue({
                code: "custom",
                path: ["pages", index, "pageNumber"],
                message: "Source page numbers must be unique",
            });
        }
        if (outputOrders.has(page.outputOrder)) {
            context.addIssue({
                code: "custom",
                path: ["pages", index, "outputOrder"],
                message: "Output page order values must be unique",
            });
        }
        pageNumbers.add(page.pageNumber);
        outputOrders.add(page.outputOrder);
    }
});
export const DocumentCapabilitySchema = z
    .object({
    capabilityId: RuleIdentifierSchema,
    kind: z.enum(["order_code", "order_term", "imaging_study", "clinical_scenario"]),
    code: z.string().min(1).max(160).optional(),
    codeSystem: z.string().min(1).max(80).optional(),
    display: z.string().min(1).max(240),
    aliases: z.array(z.string().min(1).max(240)).max(100).default([]),
    fieldIds: z.array(RuleIdentifierSchema).max(100).default([]),
})
    .strict()
    .superRefine((capability, context) => {
    if (capability.kind === "order_code" &&
        (capability.code === undefined || capability.codeSystem === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Order-code capabilities require an exact code and code system",
        });
    }
});
export const DocumentGuidanceSchema = z
    .object({
    documentId: IdSchema,
    documentVersion: VersionSchema,
    sourceDocumentHash: Sha256Schema,
    applicability: z.array(NonEmptyLimitedStringSchema).max(100),
    fieldInstructions: z
        .array(z
        .object({
        fieldId: RuleIdentifierSchema,
        instruction: NonEmptyLimitedStringSchema,
        required: z.boolean(),
        prohibitedValues: z.array(z.string().min(1).max(240)).max(100).default([]),
    })
        .strict())
        .max(1_000),
    pageInstructions: z
        .array(z
        .object({
        pageNumber: z.number().int().positive(),
        instruction: NonEmptyLimitedStringSchema,
    })
        .strict())
        .max(500),
    prohibitions: z.array(NonEmptyLimitedStringSchema).max(100).default([]),
    evidence: z
        .array(z
        .object({
        pageNumber: z.number().int().positive(),
        source: z.enum(["form_label", "form_instruction", "reference_page", "admin"]),
        summary: NonEmptyLimitedStringSchema,
    })
        .strict())
        .max(500),
    conflicts: z
        .array(z
        .object({
        code: RuleIdentifierSchema,
        summary: NonEmptyLimitedStringSchema,
        resolution: z.string().min(1).max(2_000).optional(),
        blocking: z.boolean(),
    })
        .strict())
        .max(100),
    markdown: z.string().min(1).max(200_000),
    contentHash: Sha256Schema,
    publishedAt: IsoDateTimeSchema,
})
    .strict();
const FIELD_DISCOVERY_INCOMPLETE_CODE = /^field_discovery\.incomplete\.(?:page_(\d+)|pages_(\d+)_(\d+))$/;
/** Consecutive page numbers grouped into runs, in page order. */
export function pageRuns(pageNumbers) {
    const runs = [];
    for (const page of [...new Set(pageNumbers)].sort((a, b) => a - b)) {
        const previous = runs.at(-1);
        if (previous !== undefined && previous.last === page - 1) {
            runs[runs.length - 1] = { first: previous.first, last: page };
        }
        else
            runs.push({ first: page, last: page });
    }
    return runs;
}
/**
 * The guidance conflict code for operational pages where field discovery found no fields: one
 * page, or a run of consecutive pages. The pages may still have fields for the admin to add.
 * Runs keep every page within the guidance conflict limit: 200 pages make at most 100 runs.
 */
export function fieldDiscoveryIncompleteCode(run) {
    return run.first === run.last
        ? `field_discovery.incomplete.page_${run.first}`
        : `field_discovery.incomplete.pages_${run.first}_${run.last}`;
}
/** The pages that guidance conflicts report as incomplete, in page order. */
export function fieldDiscoveryIncompletePages(conflicts) {
    const pages = new Set();
    for (const { code } of conflicts) {
        const match = FIELD_DISCOVERY_INCOMPLETE_CODE.exec(code);
        if (match === null)
            continue;
        const first = Number(match[1] ?? match[2]);
        const last = Number(match[1] ?? match[3]);
        // A page manifest has at most 500 pages; anything wider is not a code this module wrote.
        if (first < 1 || last < first || last > 500)
            continue;
        for (let page = first; page <= last; page += 1)
            pages.add(page);
    }
    return [...pages].sort((a, b) => a - b);
}
export const BlankFormPrivacyFieldTypeSchema = z.enum([
    "text",
    "checkbox",
    "radio",
    "dropdown",
    "signature",
    "unknown",
]);
/**
 * One value-free privacy finding: where it was seen, what kind it is, and for a native form
 * field its field type. Never the matched text, OCR excerpt, field name or value.
 */
export const BlankFormPrivacyFindingSchema = z
    .object({
    channel: z.enum(["native_field", "searchable_text", "raster_ocr"]),
    pageNumber: z.number().int().positive(),
    code: z.enum([
        "native_value",
        "labelled_person_name",
        "date_of_birth",
        "health_number",
        "medical_record_number",
        "patient_contact",
        "patient_address",
    ]),
    fieldType: BlankFormPrivacyFieldTypeSchema.optional(),
})
    .strict()
    .superRefine((finding, context) => {
    const native = finding.channel === "native_field";
    if (native !== (finding.code === "native_value") ||
        native !== (finding.fieldType !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "Only native form-field findings carry native_value and a field type",
        });
    }
});
export const PRIVACY_REVIEW_CONFIRMATION_TEXT_VERSION = "clinic-document-privacy-review-v1";
/** The uploading admin's confirmation, recorded in place of a cleared privacy result. */
export const BlankFormPrivacyReviewAttestationSchema = z
    .object({
    confirmationTextVersion: z.literal(PRIVACY_REVIEW_CONFIRMATION_TEXT_VERSION),
    acknowledgedAt: IsoDateTimeSchema,
})
    .strict();
/**
 * Hash-only privacy evidence produced inside the isolated scanner. OCR text and matched values are
 * intentionally omitted so a mistaken patient upload does not create another PHI-bearing object.
 */
export const BlankFormPrivacyEvidenceSchema = z
    .object({
    /**
     * `cleared`: nothing found. `attested`: findings remain but the uploading admin confirmed
     * exactly these on these bytes. `blocked`: findings with no confirmation.
     */
    status: z.enum(["cleared", "attested", "blocked"]),
    methodVersion: z.literal("clinic-document-blank-form-privacy-v1"),
    inspectedWorkingDocumentHash: Sha256Schema,
    pageCount: z.number().int().positive().max(500),
    nativeFieldValueCount: z.number().int().nonnegative().max(5_000),
    searchableTextHash: Sha256Schema,
    rasterOcrTextHash: Sha256Schema,
    rasterOcr: z
        .object({
        engine: z.literal("Tesseract"),
        engineVersion: z.string().min(1).max(80),
        language: z.literal("eng"),
        pageCount: z.number().int().positive().max(500),
    })
        .strict(),
    findings: z.array(BlankFormPrivacyFindingSchema).max(1_000),
    reviewAttestation: BlankFormPrivacyReviewAttestationSchema.optional(),
})
    .strict()
    .superRefine((inspection, context) => {
    if (inspection.rasterOcr.pageCount !== inspection.pageCount) {
        context.addIssue({
            code: "custom",
            path: ["rasterOcr", "pageCount"],
            message: "Blank-form OCR must cover every sanitized PDF page",
        });
    }
    const hasFinding = inspection.nativeFieldValueCount > 0 || inspection.findings.length > 0;
    if ((inspection.status === "cleared") === hasFinding) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Blank-form privacy status must agree with its deterministic findings",
        });
    }
    if ((inspection.status === "attested") !== (inspection.reviewAttestation !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["reviewAttestation"],
            message: "Only admin-attested privacy evidence carries the review attestation",
        });
    }
});
/**
 * The privacy gate is passed when nothing was found or the uploading admin attested the exact
 * findings. Passing it never authorizes model submission on its own.
 */
export function isBlankFormPrivacyResolved(privacy) {
    return privacy?.status === "cleared" || privacy?.status === "attested";
}
export const BlankFormPrivacyInspectionSchema = BlankFormPrivacyEvidenceSchema.safeExtend({
    analysisManifest: DocumentStorageObjectRefSchema,
}).superRefine((inspection, context) => {
    if (inspection.analysisManifest.bucket !== "clinic-document-working" ||
        inspection.analysisManifest.contentType !== "application/json" ||
        !inspection.analysisManifest.objectName.endsWith("/analysis/manifest.json")) {
        context.addIssue({
            code: "custom",
            path: ["analysisManifest"],
            message: "Blank-form privacy evidence requires the exact private analysis manifest",
        });
    }
});
export const ScannerAttestationSchema = z
    .object({
    scanJobId: IdSchema,
    sourceDocumentHash: Sha256Schema,
    status: z.enum(["clean", "infected", "rejected", "error"]),
    scanner: z
        .object({
        engine: z.string().min(1).max(160),
        engineVersion: z.string().min(1).max(80),
        definitionsVersion: z.string().min(1).max(160),
    })
        .strict(),
    threats: z.array(z.string().min(1).max(500)).max(100),
    pdfInspection: z
        .object({
        pageCount: z.number().int().positive().max(500),
        passwordMode: z.enum(["none", "empty_password", "locked"]),
        permitsFilling: z.boolean(),
        permitsPrinting: z.boolean(),
        containedJavaScript: z.boolean(),
        containedLaunchActions: z.boolean(),
        containedSubmitActions: z.boolean(),
        containedEmbeddedFiles: z.boolean(),
    })
        .strict(),
    sanitizedDocumentHash: Sha256Schema.optional(),
    blankFormPrivacy: BlankFormPrivacyInspectionSchema.optional(),
    evidenceHash: Sha256Schema,
    scannedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((attestation, context) => {
    if (attestation.status === "clean" &&
        (attestation.threats.length > 0 || attestation.pdfInspection.passwordMode === "locked")) {
        context.addIssue({
            code: "custom",
            message: "A clean scanner attestation cannot contain threats or a locked PDF",
        });
    }
    if (attestation.status === "infected" && attestation.threats.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["threats"],
            message: "An infected scanner attestation requires threat evidence",
        });
    }
    if (attestation.blankFormPrivacy !== undefined &&
        attestation.sanitizedDocumentHash !==
            attestation.blankFormPrivacy.inspectedWorkingDocumentHash) {
        context.addIssue({
            code: "custom",
            path: ["blankFormPrivacy", "inspectedWorkingDocumentHash"],
            message: "Blank-form privacy evidence must bind the exact sanitized PDF",
        });
    }
});
export const PRIVACY_REVIEW_EXPIRED_FAILURE_CODE = "PRIVACY_REVIEW_EXPIRED";
export const PRIVACY_REVIEW_DISCARDED_FAILURE_CODE = "PRIVACY_REVIEW_DISCARDED";
export const ClinicDocumentPrivacyReviewPageSchema = DocumentStorageObjectRefSchema.safeExtend({
    pageNumber: z.number().int().positive().max(500),
}).superRefine((page, context) => {
    if (page.bucket !== "clinic-document-quarantine" ||
        page.contentType !== "image/png" ||
        !(page.objectName.endsWith(`/privacy-review/page-${String(page.pageNumber).padStart(4, "0")}.png`) ||
            new RegExp(`/privacy-review/(?:initial|[0-9]{14,20})/page-${String(page.pageNumber).padStart(4, "0")}-${page.contentHash.slice(7)}\\.png$`).test(page.objectName))) {
        context.addIssue({
            code: "custom",
            message: "Privacy review pages are quarantine-only renders of the flagged page",
        });
    }
});
/**
 * Possible patient data the scanner found on an otherwise safe file. Value-free: finding codes,
 * pages and field types, plus quarantine renders of the flagged pages. The uploading admin
 * approves or discards it before `expiresAt`.
 */
export const ClinicDocumentPrivacyReviewSchema = z
    .object({
    methodVersion: z.literal("clinic-document-blank-form-privacy-v1"),
    pageCount: z.number().int().positive().max(500),
    nativeFieldValueCount: z.number().int().nonnegative().max(5_000),
    findings: z.array(BlankFormPrivacyFindingSchema).min(1).max(1_000),
    reviewPages: z.array(ClinicDocumentPrivacyReviewPageSchema).max(500),
    expiresAt: IsoDateTimeSchema,
    acknowledgedAt: IsoDateTimeSchema.optional(),
    acknowledgedBy: IdSchema.optional(),
})
    .strict();
/**
 * What approval sends to the scanner with the re-queued scan: the exact findings the uploading
 * admin reviewed, bound to the raw source SHA-256 the scanner downloads.
 */
export const ClinicDocumentPrivacyAcknowledgementSchema = z
    .object({
    confirmationTextVersion: z.literal(PRIVACY_REVIEW_CONFIRMATION_TEXT_VERSION),
    methodVersion: z.literal("clinic-document-blank-form-privacy-v1"),
    sourceSha256: z.string().regex(/^[0-9a-f]{64}$/),
    findings: z.array(BlankFormPrivacyFindingSchema).min(1).max(1_000),
    acknowledgedAt: IsoDateTimeSchema,
})
    .strict();
export const ClinicDocumentScanJobSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    documentVersion: VersionSchema,
    uploadObjectId: IdSchema,
    sourceDocumentHash: Sha256Schema,
    status: z.enum([
        "queued",
        "scanning",
        "privacy_review",
        "clean",
        "infected",
        "rejected",
        "error",
    ]),
    /** The uploading admin: the only person who may approve a privacy review. */
    requestedBy: IdSchema.optional(),
    privacyReview: ClinicDocumentPrivacyReviewSchema.optional(),
    attemptCount: z.number().int().nonnegative().max(100),
    failureCode: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{0,79}$/)
        .optional(),
    failureDetail: z.string().min(1).max(4_000).optional(),
    scannerAttestation: ScannerAttestationSchema.optional(),
    requestedAt: IsoDateTimeSchema,
    startedAt: IsoDateTimeSchema.optional(),
    completedAt: IsoDateTimeSchema.optional(),
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((job, context) => {
    if (job.status === "clean" && job.scannerAttestation?.status !== "clean") {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation"],
            message: "A clean scan job requires its exact clean scanner attestation",
        });
    }
    if (job.scannerAttestation !== undefined &&
        (job.scannerAttestation.scanJobId !== job.id ||
            job.scannerAttestation.sourceDocumentHash !== job.sourceDocumentHash)) {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation"],
            message: "Scanner attestations must bind the exact scan job and source hash",
        });
    }
    if (job.status === "clean" &&
        job.scannerAttestation?.status === "clean" &&
        job.scannerAttestation.sanitizedDocumentHash === undefined) {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation", "sanitizedDocumentHash"],
            message: "A clean scan job requires the sanitized working-copy hash",
        });
    }
    if (["infected", "rejected", "error"].includes(job.status) &&
        (job.failureCode === undefined || job.failureDetail === undefined)) {
        context.addIssue({
            code: "custom",
            message: "A failed scan job requires a stable failure code and resumable detail",
        });
    }
    if (job.status === "privacy_review" && job.privacyReview === undefined) {
        context.addIssue({
            code: "custom",
            path: ["privacyReview"],
            message: "A scan job in privacy review requires its value-free findings",
        });
    }
});
export const ClinicDocumentSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    category: DocumentCategorySchema,
    name: z.string().min(2).max(160),
    description: z.string().max(2_000).optional(),
    jurisdiction: z.string().min(2).max(80),
    tags: z.array(RuleIdentifierSchema).max(50).default([]),
    status: DocumentStatusSchema,
    isCategoryDefault: z.boolean(),
    currentPublishedVersion: VersionSchema.optional(),
    currentPublishedVersionHash: Sha256Schema.optional(),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((document, context) => {
    const hasPublishedBinding = document.currentPublishedVersion !== undefined ||
        document.currentPublishedVersionHash !== undefined;
    if (hasPublishedBinding &&
        (document.currentPublishedVersion === undefined ||
            document.currentPublishedVersionHash === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Published document bindings require both version and hash",
        });
    }
    if (document.status === "active" && !hasPublishedBinding) {
        context.addIssue({
            code: "custom",
            message: "Active documents require an exact published version binding",
        });
    }
    if (document.isCategoryDefault && document.status !== "active") {
        context.addIssue({
            code: "custom",
            path: ["isCategoryDefault"],
            message: "Only active documents may be category defaults",
        });
    }
});
export const ClinicDocumentVersionSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    version: VersionSchema,
    status: DocumentVersionStatusSchema,
    source: DocumentStorageObjectRefSchema,
    workingCopy: DocumentStorageObjectRefSchema.optional(),
    scannerAttestation: ScannerAttestationSchema.optional(),
    fields: z.array(ClinicDocumentFieldBindingSchema).max(5_000),
    pageManifest: ClinicDocumentPageManifestSchema,
    capabilities: z.array(DocumentCapabilitySchema).max(2_000),
    guidance: DocumentGuidanceSchema.optional(),
    mapper: z
        .object({
        provider: z.string().min(1).max(80),
        modelId: z.string().min(1).max(160),
        promptVersion: z.string().min(1).max(160),
        analyzedAt: IsoDateTimeSchema,
    })
        .strict()
        .optional(),
    suggestedName: z.string().min(1).max(160).optional(),
    suggestedCategory: DocumentCategorySchema.optional(),
    adminReview: z
        .object({
        reviewedBy: IdSchema,
        reviewedAt: IsoDateTimeSchema,
        decision: z.literal("approved"),
    })
        .strict()
        .optional(),
    supersedesVersion: VersionSchema.optional(),
    contentHash: Sha256Schema,
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((version, context) => {
    if (version.supersedesVersion !== undefined && version.supersedesVersion >= version.version) {
        context.addIssue({
            code: "custom",
            path: ["supersedesVersion"],
            message: "A version may supersede only an earlier document version",
        });
    }
    const fieldIds = new Set();
    for (const [index, field] of version.fields.entries()) {
        if (field.pageNumber > version.pageManifest.sourcePageCount) {
            context.addIssue({
                code: "custom",
                path: ["fields", index, "pageNumber"],
                message: "Mapped field page exceeds the immutable source page count",
            });
        }
        if (fieldIds.has(field.fieldId)) {
            context.addIssue({
                code: "custom",
                path: ["fields", index, "fieldId"],
                message: "Mapped field identifiers must be unique within a version",
            });
        }
        fieldIds.add(field.fieldId);
    }
    if (version.guidance !== undefined) {
        if (version.guidance.documentId !== version.documentId ||
            version.guidance.documentVersion !== version.version ||
            version.guidance.sourceDocumentHash !== version.source.contentHash) {
            context.addIssue({
                code: "custom",
                path: ["guidance"],
                message: "Document guidance must bind to the exact document version and source hash",
            });
        }
    }
    if (version.scannerAttestation !== undefined &&
        version.scannerAttestation.sourceDocumentHash !== version.source.contentHash) {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation", "sourceDocumentHash"],
            message: "Scanner evidence must bind to the immutable source PDF hash",
        });
    }
    if (version.scannerAttestation?.status === "clean" &&
        (version.workingCopy === undefined ||
            version.scannerAttestation.sanitizedDocumentHash !== version.workingCopy.contentHash)) {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation", "sanitizedDocumentHash"],
            message: "A clean scan must bind to the exact sanitized working-copy hash",
        });
    }
    if (version.status === "published" &&
        (version.workingCopy === undefined ||
            version.scannerAttestation?.status !== "clean" ||
            version.guidance === undefined ||
            version.mapper === undefined ||
            version.adminReview === undefined ||
            version.fields.some((field) => field.reviewStatus !== "reviewed" &&
                !isExplicitlyExcludedClinicDocumentPage(version.pageManifest.pages.find((page) => page.pageNumber === field.pageNumber))))) {
        context.addIssue({
            code: "custom",
            message: "Published versions require a sanitized working copy, clean scan, mapper provenance, reviewed fields, guidance, and admin review",
        });
    }
    if (version.status === "published" &&
        version.scannerAttestation?.status === "clean" &&
        !isBlankFormPrivacyResolved(version.scannerAttestation.blankFormPrivacy)) {
        context.addIssue({
            code: "custom",
            path: ["scannerAttestation", "blankFormPrivacy"],
            message: "Published versions require cleared or admin-attested blank-form privacy evidence on the clean scan",
        });
    }
    const knownFieldIds = new Set(version.fields.map((field) => field.fieldId));
    for (const [index, capability] of version.capabilities.entries()) {
        if (capability.fieldIds.some((fieldId) => !knownFieldIds.has(fieldId))) {
            context.addIssue({
                code: "custom",
                path: ["capabilities", index, "fieldIds"],
                message: "Capability field references must resolve to mapped fields",
            });
        }
    }
    if (version.guidance !== undefined) {
        for (const [index, instruction] of version.guidance.fieldInstructions.entries()) {
            if (!knownFieldIds.has(instruction.fieldId)) {
                context.addIssue({
                    code: "custom",
                    path: ["guidance", "fieldInstructions", index, "fieldId"],
                    message: "Guidance field references must resolve to mapped fields",
                });
            }
        }
        for (const [index, instruction] of version.guidance.pageInstructions.entries()) {
            if (instruction.pageNumber > version.pageManifest.sourcePageCount) {
                context.addIssue({
                    code: "custom",
                    path: ["guidance", "pageInstructions", index, "pageNumber"],
                    message: "Guidance page references must stay within the source page count",
                });
            }
        }
    }
});
export const DocumentRulePredicateSchema = z.lazy(() => z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("order_code"),
        codes: z.array(z.string().min(1).max(160)).min(1).max(100),
        match: z.enum(["any", "all"]),
    })
        .strict(),
    z
        .object({
        kind: z.literal("order_text"),
        terms: z.array(z.string().min(1).max(240)).min(1).max(100),
        match: z.enum(["any", "all"]),
    })
        .strict(),
    z
        .object({
        kind: z.literal("clinical_scenario"),
        scenarios: z.array(RuleIdentifierSchema).min(1).max(100),
        match: z.enum(["any", "all"]),
    })
        .strict(),
    z
        .object({
        kind: z.literal("jurisdiction"),
        value: z.string().min(2).max(80),
    })
        .strict(),
    z
        .object({
        kind: z.literal("all"),
        predicates: z.array(DocumentRulePredicateSchema).min(1).max(20),
    })
        .strict(),
    z
        .object({
        kind: z.literal("any"),
        predicates: z.array(DocumentRulePredicateSchema).min(1).max(20),
    })
        .strict(),
    z.object({ kind: z.literal("not"), predicate: DocumentRulePredicateSchema }).strict(),
]));
export const DocumentRuleActionSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("require_documents"),
        documentIds: z.array(IdSchema).min(1).max(20),
        removable: z.literal(false),
    })
        .strict(),
    z
        .object({
        kind: z.literal("recommend_documents"),
        documentIds: z.array(IdSchema).min(1).max(20),
    })
        .strict(),
    z
        .object({
        kind: z.literal("exclude_documents"),
        documentIds: z.array(IdSchema).min(1).max(20),
    })
        .strict(),
    z
        .object({
        kind: z.literal("warn"),
        code: RuleIdentifierSchema,
        message: z.string().min(1).max(2_000),
        blocking: z.boolean(),
    })
        .strict(),
]);
export const DocumentRuleSchema = z
    .object({
    ruleId: RuleIdentifierSchema,
    priority: z.number().int().min(0).max(10_000),
    enabled: z.boolean(),
    predicate: DocumentRulePredicateSchema,
    actions: z.array(DocumentRuleActionSchema).min(1).max(20),
    rationale: z.string().min(1).max(2_000),
})
    .strict();
export const ClinicDocumentRuleSetSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: VersionSchema,
    status: z.enum(["draft", "published", "superseded", "archived"]),
    sourceInstructions: z.array(z.string().min(1).max(4_000)).min(1).max(200),
    rules: z.array(DocumentRuleSchema).max(500),
    unresolvedIssues: z
        .array(z
        .object({
        code: RuleIdentifierSchema,
        message: z.string().min(1).max(2_000),
        blocking: z.boolean(),
    })
        .strict())
        .max(200),
    markdown: z.string().min(1).max(500_000),
    contentHash: Sha256Schema,
    compiledBy: IdSchema,
    compiledAt: IsoDateTimeSchema,
    publishedAt: IsoDateTimeSchema.optional(),
    supersedesVersion: VersionSchema.optional(),
})
    .strict()
    .superRefine((ruleSet, context) => {
    const ids = new Set();
    for (const [index, rule] of ruleSet.rules.entries()) {
        if (ids.has(rule.ruleId)) {
            context.addIssue({
                code: "custom",
                path: ["rules", index, "ruleId"],
                message: "Rule identifiers must be unique within a rule set",
            });
        }
        ids.add(rule.ruleId);
    }
    if (ruleSet.status === "published") {
        if (ruleSet.publishedAt === undefined) {
            context.addIssue({
                code: "custom",
                path: ["publishedAt"],
                message: "Published rule sets require a publication timestamp",
            });
        }
        if (ruleSet.unresolvedIssues.some((issue) => issue.blocking)) {
            context.addIssue({
                code: "custom",
                path: ["unresolvedIssues"],
                message: "Rule sets with blocking compilation issues cannot be published",
            });
        }
    }
    if (ruleSet.supersedesVersion !== undefined && ruleSet.supersedesVersion >= ruleSet.version) {
        context.addIssue({
            code: "custom",
            path: ["supersedesVersion"],
            message: "A rule set may supersede only an earlier version",
        });
    }
});
export const VersionedClinicalRecordBindingSchema = z
    .object({
    id: IdSchema,
    version: VersionSchema,
    contentHash: Sha256Schema,
})
    .strict();
/**
 * One already-accepted, non-revoked laboratory or imaging order projected for an
 * encounter. Every field is required: this is the authoritative binding a
 * requisition is initiated from, so a partially populated projection must fail
 * at the transport boundary rather than reach the clinician's picker. `kind`
 * excludes "standalone" so a free-text request can never be represented as an
 * accepted order.
 */
export const AcceptedClinicDocumentOrderSchema = z
    .object({
    kind: z.enum(["lab", "imaging"]),
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    actionId: IdSchema,
    actionVersion: VersionSchema,
    payloadHash: Sha256Schema,
    approvalReceiptId: IdSchema,
    /**
     * When the order's approval receipt stops being sealable. The sealed-state
     * trigger requires the receipt to be unexpired at signing time, so an order
     * past this instant can be selected and generated but never sealed — a
     * caller must not offer it for preparation.
     */
    approvalExpiresAt: IsoDateTimeSchema,
    /** Only this approver can seal an artifact bound to this order. */
    approverId: IdSchema,
    codes: z.array(z.string().min(1).max(160)).max(200),
    terms: z.array(z.string().min(1).max(240)).max(200),
})
    .strict();
export const AcceptedClinicDocumentOrderListSchema = z
    .array(AcceptedClinicDocumentOrderSchema)
    .max(50);
export const DocumentRequestContextSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    patient: VersionedClinicalRecordBindingSchema,
    encounter: VersionedClinicalRecordBindingSchema,
    provider: VersionedClinicalRecordBindingSchema,
    clinic: VersionedClinicalRecordBindingSchema,
    jurisdiction: z.string().min(2).max(80),
    clinicalScenarios: z.array(RuleIdentifierSchema).max(100).default([]),
    preparationSource: VersionedClinicalRecordBindingSchema.optional(),
    /**
     * Tests suggested by clinic instructions that the preparing physician explicitly chose not
     * to order, as sorted concept identifiers. They are bound into the selection's request hash,
     * so signing re-checks them through the existing preparation pins. Optional rather than
     * defaulted, and absent when nothing was declined, so every context stored before this field
     * existed keeps its exact hash.
     */
    declinedConceptIds: z
        .array(z.string().trim().min(1).max(160))
        .min(1)
        .max(100)
        .refine((ids) => ids.every((id, index) => index === 0 || (ids[index - 1] ?? "") < id), "Declined tests must be listed once each, in sorted order")
        .optional(),
    order: z
        .object({
        kind: z.enum(["lab", "imaging", "standalone"]),
        actionId: IdSchema.optional(),
        actionVersion: VersionSchema.optional(),
        payloadHash: Sha256Schema.optional(),
        approvalReceiptId: IdSchema.optional(),
        reviewBinding: z
            .object({
            bundleId: IdSchema,
            bundleVersion: VersionSchema,
            bundleContentHash: Sha256Schema,
            exactPreviewHash: Sha256Schema,
            mode: z.literal("approved_execution"),
        })
            .strict()
            .optional(),
        codes: z.array(z.string().min(1).max(160)).max(200).default([]),
        terms: z.array(z.string().min(1).max(240)).max(200).default([]),
        /** Server-rebuilt clinical intent from the exact reviewed/accepted order, or explicit standalone purpose. */
        clinicalInformation: z.string().trim().min(1).max(4_000).optional(),
        /**
         * The ordered tests or imaging studies as code/system pairs, which is what a coded field
         * binding matches against. `codes` and `terms` are deliberately left
         * untouched beside it: every existing consumer reads those, and the
         * legacy fuzzy display-term match still governs uncoded bindings.
         *
         * Server-derived. Whatever a caller sends is discarded and rebuilt from
         * the accepted-order authority or the exact reviewed action, so a client
         * can never inject a code pairing and tick a box the order never asked
         * for. Empty on callers that predate coding, which simply means every
         * binding falls back to the fuzzy match.
         */
        codedTests: z
            .array(z
            .object({
            code: z.string().trim().min(1).max(160),
            codeSystem: z.enum(["LOINC", "local", "RadLex", "free_text"]),
            display: z.string().trim().min(1).max(240),
        })
            .strict())
            .max(200)
            // Optional rather than defaulted: a default is materialized on parse,
            // which would make this required in the output type and force every
            // existing order-context constructor to be edited for a field that
            // means "no coded tests" when absent.
            .optional(),
        imagingStudies: z.array(DocumentImagingStudyIdentitySchema).max(20).optional(),
        /** Rebuilt from the exact approved imaging template; never accepted as caller routing. */
        imagingForm: ImagingFacilityFormReferenceSchema.optional(),
        /**
         * The clinic order set the reviewed or accepted lab order was drafted from. Server-derived
         * like `codedTests`, and absent when there is none, so existing contexts keep their hash.
         */
        orderSetId: FormOrderSetIdSchema.optional(),
        /**
         * The exact form a physician picked for a "+" lab order, which the order's forms always
         * include. Lab requests only. Server-derived like `orderSetId`: rebuilt from the reviewed
         * or accepted order's own `form`, never taken from a caller. Absent on every other
         * request, so every context stored before this field existed keeps its exact hash.
         */
        labForm: ManualOrderFormRefSchema.optional(),
        /**
         * The exact published form a physician picked for a "+" document. Standalone requests
         * only, and absent on every other request, so every context stored before this field
         * existed keeps its exact hash. Which categories may be picked is the vault's rule, not
         * this schema's, because this schema also parses stored selections.
         */
        requestedDocument: ManualOrderFormRefSchema.optional(),
        /**
         * The "+" order the picked form is signed for, always beside `requestedDocument` and never
         * without it: the database marks only this order signed with the document. Absent on every
         * other request, like `requestedDocument`, so stored contexts keep their exact hash.
         */
        manualOrderId: ManualOrderIdSchema.optional(),
        categoryHint: DocumentCategorySchema.optional(),
    })
        .strict()
        .superRefine((order, context) => {
        if (order.labForm !== undefined && order.kind !== "lab") {
            context.addIssue({
                code: "custom",
                path: ["labForm"],
                message: "Only a lab order request names the lab form a physician picked",
            });
        }
        if (order.requestedDocument !== undefined && order.kind !== "standalone") {
            context.addIssue({
                code: "custom",
                path: ["requestedDocument"],
                message: "Only a standalone document request names the form a physician picked",
            });
        }
        if ((order.manualOrderId === undefined) !== (order.requestedDocument === undefined)) {
            context.addIssue({
                code: "custom",
                path: ["manualOrderId"],
                message: "A picked form names the order it is signed for, and only a picked form does",
            });
        }
        const hasActionBinding = order.actionId !== undefined ||
            order.actionVersion !== undefined ||
            order.payloadHash !== undefined;
        if (hasActionBinding &&
            (order.actionId === undefined ||
                order.actionVersion === undefined ||
                order.payloadHash === undefined)) {
            context.addIssue({
                code: "custom",
                message: "Structured orders require an exact action version and payload hash binding",
            });
        }
        if (order.kind !== "standalone" && !hasActionBinding) {
            context.addIssue({
                code: "custom",
                message: "Lab and imaging document requests require a structured action binding",
            });
        }
        if (order.approvalReceiptId !== undefined && !hasActionBinding) {
            context.addIssue({
                code: "custom",
                message: "An order approval receipt requires an exact structured action binding",
            });
        }
        if (order.reviewBinding !== undefined && !hasActionBinding) {
            context.addIssue({
                code: "custom",
                message: "A reviewed order preview requires an exact structured action binding",
            });
        }
        if (order.kind !== "standalone" &&
            order.approvalReceiptId === undefined &&
            order.reviewBinding === undefined) {
            context.addIssue({
                code: "custom",
                message: "Lab and imaging documents require either an accepted-order receipt or an exact review binding",
            });
        }
        if (order.kind === "standalone" &&
            (order.approvalReceiptId !== undefined || order.reviewBinding !== undefined)) {
            context.addIssue({
                code: "custom",
                message: "Standalone document requests cannot claim a clinical-order approval binding",
            });
        }
        if (order.approvalReceiptId !== undefined && order.reviewBinding !== undefined) {
            context.addIssue({
                code: "custom",
                message: "A document request must use either review or accepted-order authority, not both",
            });
        }
    }),
    requestedAt: IsoDateTimeSchema,
})
    .strict();
export const DocumentSelectionBindingSchema = z
    .object({
    documentId: IdSchema,
    documentVersion: VersionSchema,
    documentVersionHash: Sha256Schema,
    guidanceHash: Sha256Schema,
    category: DocumentCategorySchema,
    required: z.boolean(),
    removable: z.boolean(),
    source: z.enum([
        "clinic_rule",
        "document_capability",
        "inference",
        "category_default",
        "manual",
    ]),
    matchedRuleIds: z.array(RuleIdentifierSchema).max(100).default([]),
    reasonCodes: z.array(RuleIdentifierSchema).min(1).max(100),
})
    .strict()
    .superRefine((binding, context) => {
    if (binding.required && binding.removable) {
        context.addIssue({
            code: "custom",
            path: ["removable"],
            message: "Required documents cannot be removable from an execution batch",
        });
    }
    if (binding.source === "clinic_rule" && binding.matchedRuleIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["matchedRuleIds"],
            message: "Clinic-rule selections require exact matched rule identifiers",
        });
    }
});
export const DocumentSelectionDecisionSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    requestHash: Sha256Schema,
    ruleSet: z
        .object({ id: IdSchema, version: VersionSchema, contentHash: Sha256Schema })
        .strict()
        .optional(),
    status: z.enum(["selected", "blocked"]),
    selectedDocuments: z.array(DocumentSelectionBindingSchema).max(50),
    warnings: z
        .array(z
        .object({
        code: RuleIdentifierSchema,
        message: z.string().min(1).max(2_000),
        blocking: z.boolean(),
    })
        .strict())
        .max(100),
    blockers: z
        .array(z
        .object({
        code: RuleIdentifierSchema,
        message: z.string().min(1).max(2_000),
    })
        .strict())
        .max(100),
    inference: z
        .object({
        provider: z.string().min(1).max(80),
        modelId: z.string().min(1).max(160),
        promptVersion: z.string().min(1).max(160),
        provenanceHash: Sha256Schema,
        configurationHash: Sha256Schema.optional(),
        // Optional for reading historical selections; current preparation requires an exact match.
        labTestMatchingVersion: z.string().min(1).max(80).optional(),
    })
        .strict()
        .optional(),
    decidedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((decision, context) => {
    if (decision.status === "selected" && decision.selectedDocuments.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["selectedDocuments"],
            message: "A selected decision must contain at least one document",
        });
    }
    if (decision.status === "blocked" && decision.blockers.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["blockers"],
            message: "A blocked decision requires at least one blocker",
        });
    }
    if (decision.status === "selected" && decision.blockers.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["blockers"],
            message: "A selected decision cannot retain unresolved blockers",
        });
    }
    if (decision.status === "selected" &&
        decision.warnings.some((warning) => warning.blocking === true)) {
        context.addIssue({
            code: "custom",
            path: ["warnings"],
            message: "A selected decision cannot carry blocking warnings",
        });
    }
    if (decision.status === "blocked" && decision.selectedDocuments.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["selectedDocuments"],
            message: "A blocked decision cannot retain selected documents",
        });
    }
    const documentIds = new Set();
    for (const [index, binding] of decision.selectedDocuments.entries()) {
        if (documentIds.has(binding.documentId)) {
            context.addIssue({
                code: "custom",
                path: ["selectedDocuments", index, "documentId"],
                message: "A document may appear only once in a selection decision",
            });
        }
        documentIds.add(binding.documentId);
    }
});
export const ProviderSignatureVersionSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    providerId: IdSchema,
    version: VersionSchema,
    status: z.enum(["active", "superseded", "revoked"]),
    image: DocumentStorageObjectRefSchema,
    validFrom: IsoDateTimeSchema,
    validUntil: IsoDateTimeSchema.optional(),
    reviewedBy: IdSchema,
    reviewedAt: IsoDateTimeSchema,
    supersedesVersion: VersionSchema.optional(),
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((signature, context) => {
    if (signature.supersedesVersion !== undefined &&
        signature.supersedesVersion >= signature.version) {
        context.addIssue({
            code: "custom",
            path: ["supersedesVersion"],
            message: "A signature version may supersede only an earlier version",
        });
    }
    if (signature.validUntil !== undefined &&
        Date.parse(signature.validUntil) <= Date.parse(signature.validFrom)) {
        context.addIssue({
            code: "custom",
            path: ["validUntil"],
            message: "Signature validity must end after it begins",
        });
    }
});
export const GeneratedDocumentArtifactSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: VersionSchema,
    selectionId: IdSchema,
    document: DocumentSelectionBindingSchema,
    patient: VersionedClinicalRecordBindingSchema,
    encounter: VersionedClinicalRecordBindingSchema,
    order: z
        .object({ actionId: IdSchema, actionVersion: VersionSchema, payloadHash: Sha256Schema })
        .strict()
        .optional(),
    state: z.enum(["draft", "ready_for_signature", "sealed", "failed", "cancelled"]),
    sourceDraft: z
        .object({ version: VersionSchema, contentHash: Sha256Schema })
        .strict()
        .optional(),
    includedPages: z.array(z.number().int().positive()).min(1).max(500),
    fieldValuesHash: Sha256Schema,
    approval: z.object({ receiptId: IdSchema, exactPayloadHash: Sha256Schema }).strict().optional(),
    providerSignature: z
        .object({
        signatureId: IdSchema,
        signatureVersion: VersionSchema,
        signatureContentHash: Sha256Schema,
        signedBy: IdSchema,
        signedAt: IsoDateTimeSchema,
    })
        .strict()
        .optional(),
    artifact: DocumentStorageObjectRefSchema.optional(),
    combinedIncludedPages: z.array(z.number().int().positive()).min(1).max(500).optional(),
    combinedArtifact: DocumentStorageObjectRefSchema.optional(),
    externalSubmissionEnabled: z.literal(false),
    sealedAt: IsoDateTimeSchema.optional(),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((artifact, context) => {
    if (artifact.state === "sealed" &&
        (artifact.approval === undefined ||
            artifact.sourceDraft === undefined ||
            artifact.providerSignature === undefined ||
            artifact.artifact === undefined ||
            artifact.combinedArtifact === undefined ||
            artifact.combinedIncludedPages === undefined ||
            artifact.sealedAt === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Sealed artifacts require source draft, approval, provider signature, patient and combined storage, and seal bindings",
        });
    }
    if (artifact.state !== "sealed" &&
        (artifact.sourceDraft !== undefined ||
            artifact.combinedArtifact !== undefined ||
            artifact.combinedIncludedPages !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "Only sealed artifacts may carry a combined-output variant",
        });
    }
    if (artifact.sourceDraft !== undefined && artifact.sourceDraft.version >= artifact.version) {
        context.addIssue({
            code: "custom",
            path: ["sourceDraft", "version"],
            message: "A sealed artifact must supersede its exact reviewed draft",
        });
    }
    if (new Set(artifact.includedPages).size !== artifact.includedPages.length) {
        context.addIssue({
            code: "custom",
            path: ["includedPages"],
            message: "Generated artifact page numbers must be unique",
        });
    }
    if (artifact.combinedIncludedPages !== undefined &&
        new Set(artifact.combinedIncludedPages).size !== artifact.combinedIncludedPages.length) {
        context.addIssue({
            code: "custom",
            path: ["combinedIncludedPages"],
            message: "Combined artifact page numbers must be unique",
        });
    }
});
const ExpectedVersionBindingSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    expectedVersionHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const EnrollProviderSignatureCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    providerId: IdSchema,
    signatureId: IdSchema,
    expectedVersion: z.number().int().nonnegative(),
    expectedVersionHash: Sha256Schema.optional(),
    image: DocumentStorageObjectRefSchema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((command, context) => {
    if (command.expectedVersion > 0 && command.expectedVersionHash === undefined) {
        context.addIssue({
            code: "custom",
            path: ["expectedVersionHash"],
            message: "Signature replacement requires the exact active version hash",
        });
    }
    if (command.expectedVersion === 0 && command.expectedVersionHash !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["expectedVersionHash"],
            message: "A first signature version cannot claim a prior version hash",
        });
    }
    if (command.image.bucket !== "provider-signatures" ||
        !["image/png", "image/jpeg"].includes(command.image.contentType) ||
        command.image.byteSize > 10 * 1024 * 1024) {
        context.addIssue({
            code: "custom",
            path: ["image"],
            message: "Provider signatures require a private PNG or JPEG no larger than 10 MB",
        });
    }
    const extension = command.image.contentType === "image/png" ? "png" : "jpg";
    const expectedObjectName = `${command.tenantId}/${command.siteId}/${command.providerId}/${command.signatureId}/${command.expectedVersion + 1}.${extension}`;
    if (command.image.objectName !== expectedObjectName ||
        command.image.objectVersion !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["image", "objectName"],
            message: "Provider signature image path must match its exact scoped version",
        });
    }
});
export const CreateClinicDocumentUploadCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    uploadObjectId: IdSchema,
    // Version 1 is a new document; a higher version re-submits the same
    // document as a superseding upload (e.g. after a rejected scan).
    documentVersion: VersionSchema.optional(),
    category: DocumentCategorySchema,
    name: z.string().min(2).max(160),
    jurisdiction: z.string().min(2).max(80),
    tags: z.array(RuleIdentifierSchema).max(50).default([]),
    originalFileName: z.string().min(1).max(240),
    sourceDocumentHash: Sha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(50 * 1024 * 1024),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const RetryClinicDocumentScanCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    scanJobId: IdSchema,
    expectedSourceDocumentHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
/**
 * The uploading admin confirms a flagged file is a blank template. Bound to the source bytes and
 * to exactly the findings they reviewed; the scan reruns and requires the same findings.
 */
export const ApproveClinicDocumentPrivacyReviewCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    scanJobId: IdSchema,
    expectedSourceDocumentHash: Sha256Schema,
    confirmationTextVersion: z.literal(PRIVACY_REVIEW_CONFIRMATION_TEXT_VERSION),
    reviewedFindings: z.array(BlankFormPrivacyFindingSchema).min(1).max(1_000),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const RequestClinicDocumentAnalysisCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    documentVersion: VersionSchema,
    includedPageNumbers: z
        .array(z.number().int().positive().max(500))
        .min(1)
        .max(500)
        .refine((pages) => new Set(pages).size === pages.length, "Form pages must be unique")
        .optional(),
    manualMapping: z.boolean().optional(),
    blankFormAttestation: z
        .object({
        confirmedBlank: z.literal(true),
        confirmationTextVersion: z.literal("clinic-document-blank-form-v1"),
        confirmedAt: IsoDateTimeSchema,
    })
        .strict(),
}).strict();
export const SaveClinicDocumentDraftCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    documentVersion: VersionSchema,
    fields: z.array(ClinicDocumentFieldBindingSchema).max(5_000),
    pageManifest: ClinicDocumentPageManifestSchema,
    capabilities: z.array(DocumentCapabilitySchema).max(2_000),
    guidance: DocumentGuidanceSchema.optional(),
    // Document-level settings that ride along with a draft save. Each is
    // applied to the parent document record atomically with the version save;
    // omitted fields leave the stored value untouched. Constraints mirror
    // ClinicDocumentSchema exactly.
    name: z.string().min(2).max(160).optional(),
    jurisdiction: z.string().min(2).max(80).optional(),
    tags: z.array(RuleIdentifierSchema).max(50).optional(),
}).strict();
export const PublishClinicDocumentVersionCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    documentVersion: VersionSchema,
    makeActive: z.boolean(),
    makeCategoryDefault: z.boolean(),
    category: DocumentCategorySchema.optional(),
    allowWithoutSignature: z.boolean().optional(),
}).strict();
export const SetClinicDocumentStatusCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    status: z.enum(["active", "inactive", "archived"]),
}).strict();
export const ForkClinicDocumentVersionCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    /** The published source version being forked; the binding hash is its content hash. */
    documentVersion: VersionSchema,
}).strict();
/**
 * Machine-readable detail tokens carried on vault policy/conflict errors
 * (HTTP 409) so clients can map a rejection to the exact gate that fired
 * instead of pattern-matching prose. Both vault implementations attach these
 * via DomainError.detail and the API error envelope exposes them verbatim.
 */
export const ClinicDocumentVaultErrorDetailSchema = z.enum([
    "clinic_document_fields_unreviewed",
    "provider_signature_required",
    "provider_signature_unpublishable",
    "clinic_document_draft_exists",
]);
export const clinicDocumentVaultErrorDetails = ClinicDocumentVaultErrorDetailSchema.enum;
export const DiscardClinicDocumentDraftCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    documentId: IdSchema,
    /** The never-published draft version being discarded. */
    documentVersion: VersionSchema,
}).strict();
export const CompileClinicDocumentRulesCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    ruleSetId: IdSchema,
    expectedVersion: z.number().int().nonnegative(),
    expectedVersionHash: Sha256Schema.optional(),
    instructions: z.array(z.string().min(1).max(4_000)).min(1).max(200),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((command, context) => {
    if (command.expectedVersion > 0 && command.expectedVersionHash === undefined) {
        context.addIssue({
            code: "custom",
            path: ["expectedVersionHash"],
            message: "Existing rule sets require their expected version hash",
        });
    }
});
export const PublishClinicDocumentRulesCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    ruleSetId: IdSchema,
    expectedVersion: VersionSchema,
    expectedVersionHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const SimulateClinicDocumentRulesCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    ruleSet: ClinicDocumentRuleSetSchema,
    context: DocumentRequestContextSchema,
})
    .strict();
export const SelectClinicDocumentsCommandSchema = z
    .object({
    context: DocumentRequestContextSchema,
    expectedRuleSetVersion: VersionSchema.optional(),
    expectedRuleSetHash: Sha256Schema.optional(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((command, context) => {
    const hasRuleBinding = command.expectedRuleSetVersion !== undefined || command.expectedRuleSetHash !== undefined;
    if (hasRuleBinding &&
        (command.expectedRuleSetVersion === undefined || command.expectedRuleSetHash === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Rule-set selection bindings require both version and hash",
        });
    }
});
/**
 * The longest key of an ordered test or imaging study the forms have no place for. An imaging
 * study's key holds its code, modality, body site, side and contrast, so it can run long.
 */
export const ORDER_TEST_KEY_MAX_LENGTH = 600;
export const GenerateClinicDocumentsCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    selectionId: IdSchema,
    expectedSelectionHash: Sha256Schema,
    manualValues: z.record(RuleIdentifierSchema, z.string().max(10_000)).default({}),
    idempotencyKey: IdempotencyKeySchema,
    /**
     * Set by a caller that lets the clinician complete the forms by hand. An ordered test that no
     * form field shows then leaves the forms in draft and is reported with the batch, instead of
     * refusing the preparation. `placedTestKeys` are the tests the clinician confirmed they added.
     */
    handCompletion: z
        .object({
        placedTestKeys: z.array(z.string().trim().min(1).max(ORDER_TEST_KEY_MAX_LENGTH)).max(40),
    })
        .strict()
        .optional(),
})
    .strict();
export const UpdateGeneratedDocumentDraftCommandSchema = ExpectedVersionBindingSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    generatedDocumentId: IdSchema,
    fieldValues: z.record(RuleIdentifierSchema, z.string().max(10_000)),
    includedPages: z.array(z.number().int().positive()).min(1).max(500),
}).strict();
export const SignAndSealClinicDocumentsCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    selectionId: IdSchema,
    expectedSelectionHash: Sha256Schema,
    generatedDocuments: z
        .array(z.object({ id: IdSchema, version: VersionSchema, contentHash: Sha256Schema }).strict())
        .min(1)
        .max(50),
    providerSignatureId: IdSchema,
    providerSignatureVersion: VersionSchema,
    providerSignatureHash: Sha256Schema,
    approvalReceiptId: IdSchema,
    exactApprovalPayloadHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const CombineGeneratedDocumentsCommandSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    selectionId: IdSchema,
    expectedSelectionHash: Sha256Schema,
    artifacts: z
        .array(z.object({ id: IdSchema, version: VersionSchema, contentHash: Sha256Schema }).strict())
        .min(1)
        .max(50),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ClinicDocumentVersionHistoryQuerySchema = z
    .object({ tenantId: IdSchema, siteId: IdSchema, documentId: IdSchema })
    .strict();
//# sourceMappingURL=clinic-documents.js.map